<template>
  <div>
    <div class="panel-heading order-heading-with-stats">
      <h4 class="panel-title">{{ $t('orders.deliveredToErbilOrdersTitle') }}</h4>

      <div class="order-heading-right">
        <div class="order-toolbar-buttons">
          <a class="order-toolbar-btn order-toolbar-btn--icon-only" :title="$t('orders.filtersToggleLabel')" @click="toggleFiltersVisible">
            <i class="material-icons">{{ showFilters ? 'expand_less' : 'expand_more' }}</i>
          </a>
          <a class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="reloadTable">
            <i class="material-icons">cached</i>
            <span v-if="showButtonLabels">{{ $t('datatable.reloadShortLabel') }}</span>
          </a>
          <a class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="sendToExport">
            <i class="material-icons">explicit</i>
            <span v-if="showButtonLabels">{{ $t('datatable.exportButton') }}</span>
          </a>
          <a v-if="$_has_perm('branch_delivered_order_generate_qrcode')" class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="openGenerateQrCodeModal()">
            <i class="fa fa-qrcode"></i>
            <span v-if="showButtonLabels">{{ $t('datatable.qrCodeShortLabel') }}</span>
          </a>
          <a v-if="$_has_perm('add_shipping_invoice')" class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="openOrderInvoiceModal()">
            <i class="fa fa-file-text-o"></i>
            <span v-if="showButtonLabels">{{ $t('datatable.invoiceShortLabel') }}</span>
          </a>
          <a v-if="$_has_perm('send_to_customer')" class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="sendAllToUser()">
            <i class="material-icons" style="color:#006b8d">send</i>
            <span v-if="showButtonLabels">{{ $t('datatable.sendAllToCustomerShortLabel') }}</span>
          </a>
          <a class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="prepareToPrint()">
            <i class="material-icons" style="color:#006b8d">print</i>
            <span v-if="showButtonLabels">{{ $t('datatable.prepareToPrintShortLabel') }}</span>
          </a>
          <a v-if="$_has_perm('all_order_view_customer_summary')" class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="openCustomerDeliverySummaryModal()">
            <i class="material-icons" style="color:#00897b">people</i>
            <span v-if="showButtonLabels">{{ $t('datatable.summaryShortLabel') }}</span>
          </a>
          <a v-if="$_has_perm('unclear_order')" class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="confirmUnclearAll()">
            <i class="material-icons" style="color:#c62828">undo</i>
            <span v-if="showButtonLabels">{{ $t('datatable.unclearButton') }}</span>
          </a>
          <a class="order-toolbar-display-toggle" @click="$_toggle_toolbar_button_display()">
            <i class="material-icons">{{ showButtonLabels ? 'label_off' : 'label' }}</i>
          </a>
        </div>

        <div class="order-stats-row order-stats-row--compact">
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--blue"><i class="material-icons">list_alt</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('reports.totalOrders') }}</span>
              <span class="order-stat-value">{{ summaryTotalOrders }}</span>
            </div>
          </div>
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--green"><i class="material-icons">attach_money</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('printLogs.totalAmountLabel') }}</span>
              <span class="order-stat-value">$ {{ summaryTotalAmount.toFixed(2) }}</span>
              <span v-if="qrCodeValues.length > 0" class="order-stat-note">({{ qrCodeValues.length }} selected)</span>
              <span v-else-if="!filterMade" class="order-stat-note">({{ $t('orders.currentPageScopeNote') }})</span>
            </div>
          </div>
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--purple"><i class="material-icons">today</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('adminDashboard.ordersTodayLabel') }}</span>
              <span class="order-stat-value">{{ ordersTodayCount }}</span>
              <span class="order-stat-note">({{ $t('orders.currentPageScopeNote') }})</span>
            </div>
          </div>
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--orange"><i class="material-icons">payment</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('adminDashboard.pendingPaymentsLabel') }}</span>
              <span class="order-stat-value">{{ pendingPaymentsCount }}</span>
              <span class="order-stat-note">({{ $t('orders.currentPageScopeNote') }})</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="panel-body"
      style="display: block"
    >
      <div class="row">
        <!-- Attach to Delivery button — commented out for now, kept for later use.
        :showAttachToDeliveryButton="true"
        :attachToDelivery="attachSelectedToDelivery"
        -->
        <!-- Delivery Invoice button — commented out for now, kept for later use.
        :addDeliveryInvoiceButton="true"
        :openDeliveryInvoiceModal="openDeliveryInvoiceModal"
        -->
        <preDatatableComponent
          ref="preDatatable"
          :permissions="{
            send_to_customer: 'send_to_customer',
            add_shipping_invoice: 'add_shipping_invoice',
            generate_qr_code: 'branch_delivered_order_generate_qrcode',
            view_customer_delivery_summary: 'all_order_view_customer_summary',
            unclear_permission: 'unclear_order'
          }"
          :removeAddButton="true"
          :removeWarehouseDeliverButton="true"
          :hideReloadButton="true"
          :collapsibleFilters="true"
          :filtersVisible="showFilters"
          :openSendToUserModal="openSendToUserModal"
          :sendAllToUser="sendAllToUser"
          :sendToExport="sendToExport"
          :tableData="tableData"
          :resetGetResults="resetGetResults"
          :searchMe="searchMe"
          :enableCustomerSearchSuggestions="true"
          :pagination="pagination"
          :perPageFix="perPageFix"
          :filterTable="filterTable"
          :prepareToPrint="prepareToPrint"
          :openGenerateQrCodeModal="openGenerateQrCodeModal"
          :openOrderInvoiceModal="openOrderInvoiceModal"
          :openCustomerDeliverySummaryModal="openCustomerDeliverySummaryModal"
          :confirmUnclearAll="confirmUnclearAll"
          :resetGetResults2="resetGetResults2"
          :columns="columns"
          :showHideColumn="showHideColumn"
          :defaultHideCol="defaultHideCol"
          :storageKey="'cols_branchDeliveredOrder_v3'"
          :resultsVersion="resultsVersion"
          :filterMade="filterMade"
          :totalResult="totalResult"
          :warehouseFilterItems="warehouses"
          :warehouseFilterChange="handleWarehouseFilter"
          @branch-edit="branchEdit"
        >
          <template slot="custom-filter-start">
            <template v-if="showFilters">
            <v-flex class="filter-line-break"></v-flex>
            <v-flex class="filter-field px-2">
              <v-select
                :items="clearedFilterItems"
                v-model="clearedFilterVal"
                :label="$t('columns.cleared')"
                hide-details
                single-line
                item-text="text"
                item-value="value"
              ></v-select>
            </v-flex>
            </template>
          </template>
        </preDatatableComponent>
        <div class="col-xs-12">
          <div class="row text-center">
            <span
              id="test-editable"
              style="opacity: 0"
            ></span>
          </div>

          <!-- Customer select + Generate Invoice button - hidden for later use, keep generateInvoiceData/customersList/generateInvoiceCustomer wiring intact
          <v-layout
            wrap
            style="margin: 0px"
          >
            <v-flex
              xs12
              sm8
              md5
            >
              <v-select
                style="width: 100%"
                autocomplete
                :items="customersList"
                v-model="generateInvoiceData.customer_id"
                :label="$t('columns.customer')"
                single-line
                clearable
              />
            </v-flex>

            <v-flex
              xs12
              sm4
              md5
              style="padding-top: 15px"
            >
              <v-btn
                :disabled="!generateInvoiceData.customer_id"
                style="height: auto; padding: 4px" class="rtl-ml-15"
                @click="generateInvoiceCustomer(generateInvoiceData.customer_id)"
                depressed
                color="primary"
                small
              >
                Generate Invoice
              </v-btn>
            </v-flex>
          </v-layout>
          -->

          <div class="table-responsive oc-table-shell">
          <datatable
            v-if="!isMobile"
            :sortDir="tableData.sortDir"
            :columns="columns"
            :sortByColumn="sortByColumn"
            :searchOneCol="searchOneCol"
            :sortKey="sortKey"
            :sortOrders="sortOrders"
            :checkAll="checkAll"
            :unCheckAll="unCheckAll"
          >
            <tbody>
              <tr
                v-for="(r, index) in apiResults"
                :key="r.order_id"
                :style="
                  qrCodeValues.find((x) => x[0] == r.order_id)
                    ? 'background-color: #FFF9C4'
                    : ((r.item_weight === 0 || r.item_weight == null) || (Number(r.is_ship_only) === 1)) && !r.order_invoice_id_fk
                    ? 'background-color: #ee7272'
                    : ''
                "
              >
                <td>
                  <input
                    :id="'cb' + index"
                    type="checkbox"
                    :value="[
                      r.order_id,
                      r.full_name,
                      r.phone_number,
                      r.user_id,
                      r.theOrderID,
                      r.is_ship_only,
                      r.credit,
                      r.product_name,
                      r.warehouse_id_fk,
                      r.transportation_type,
                      r.is_ship_only,
                      r.total_amount,
                      r.local_delivery,
                      r.note,
                      r.size,
                      r.payment_status,
                      r.order_invoice_number,
                    ]"
                    v-model="qrCodeValues"
                  />
                  <!-- r.deliver_branch_name,
                      r.deliver_branch_id_fk, -->
                </td>
                <td class="oc-cell hover-container">
                  <div class="oc-order-number">
                    <i v-if="r.qrcode" class="fa fa-qrcode" style="color:#388E3C" aria-hidden="true"></i> #{{ r.theOrderID }}
                  </div>
                  <div class="oc-order-sub" v-if="r.qrcode"><i class="fa fa-qrcode" aria-hidden="true"></i> {{ r.qrcode }}</div>
                  <div class="oc-order-sub" v-if="r.track_number">{{ $t('columns.trackNumber') }}: {{ r.track_number }}</div>
                  <div class="oc-order-sub" v-if="r.last_order_invoice">{{ $t('columns.invoiceNumber') }}: {{ r.last_order_invoice }}</div>
                  <div class="oc-order-sub"><view-order-users :orderData="r" /></div>
                </td>
                <td class="oc-cell ws-nowrap">
                  <customer-device-icon :orderSource="r.order_source" />
                  <img v-if="r.profile_image" :src="r.profile_image.startsWith('http') ? r.profile_image : `${$_image_path()}${r.profile_image}`" class="customer-profile-image" />
                  <span class="customer-name-stack">
                    <span v-if="r.full_name && r.full_name.trim().split(/\s+/).length > 3" style="word-break: break-word; white-space: pre-line;">
                      {{ r.full_name.replace(/((?:\S+\s+){3})/g, '$1\n') }}
                    </span>
                    <span v-else>{{ r.full_name }}</span>
                    <span v-if="r.customer_id_fk" class="oc-customer-id">ID: {{ r.customer_id_fk }}</span>
                    <span v-if="r.phone_number" class="customer-phone-line">{{ r.phone_number }}</span>
                    <span v-if="r.phone_number_2" class="customer-phone-line">{{ r.phone_number_2 }}</span>
                  </span>
                  <open-whatsapp-and-viber :phoneNumber="r.phone_number" />
                </td>
                <td class="oc-cell">
                  <div class="oc-item-name">{{ r.product_name ? r.product_name.substr(0, 35) + (r.product_name.length > 35 ? '...' : '') : '—' }}</div>
                  <div class="oc-item-sub" v-if="r.brand">{{ $t('columns.brand') }}: <strong>{{ r.brand }}</strong></div>
                  <a v-if="r.product_url" :href="r.product_url" target="_blank" rel="noopener noreferrer" class="oc-item-link">
                    <i class="material-icons">link</i> {{ $t('columns.url') }}
                  </a>
                </td>
                <td class="oc-cell oc-product-cell">
                  <img
                    v-if="r.image"
                    :src="r.image.startsWith('http') ? r.image : `${$_image_path()}${r.image}`"
                    class="oc-thumb"
                    @click="lightboxImage = r.image.startsWith('http') ? r.image : `${$_image_path()}${r.image}`; lightboxOpen = true"
                  />
                  <span v-else class="oc-thumb-placeholder">—</span>
                </td>
                <td class="oc-cell">
                  <span class="oc-status-chip" style="background:#685AFF; color:#FFFFFF;">{{ $_status_label(r.status) }}</span>
                  <span
                    v-if="r.cleared_at"
                    class="oc-status-chip rtl-ml-2"
                    style="background-color:#FFF9C4; color:#7A6A00"
                  >cleared</span>
                  <span
                    v-if="r.is_ship_only == 1"
                    class="oc-status-chip rtl-ml-2"
                    style="background-color:#FCE4EC; color:#C2185B;"
                  >{{ $t('columns.shipOnly') }}</span>
                  <div class="oc-location-line" v-if="r.country">
                    <i class="material-icons oc-location-icon">place</i>{{ r.country }}<span v-if="r.transportation_type_obj"> · {{ r.transportation_type_obj.name.replace('Transportation', '') }}</span>
                  </div>
                  <div class="oc-item-sub" v-if="r.delivery_confirmed && r.delivery_type === 'office delivery'">{{ $t('delivery.pickupLabel') }}</div>
                  <div class="oc-item-sub" v-else-if="r.delivery_type === 'home delivery'">{{ $t('orders.homeDelivery') }}</div>
                  <div class="oc-item-sub" v-if="r.purchase_note">{{ $t('columns.purchaseNote') }}: {{ r.purchase_note }}</div>
                </td>
                <td class="combined-cell-td oc-cell">
                  <div class="combined-cell">
                    <div><span class="combined-cell-label">{{ $t('columns.price') }}:</span> {{ r.item_price ? '$' + r.item_price : '—' }}</div>
                    <div><span class="combined-cell-label">{{ $t('columns.shipping') }}:</span> {{ r.internal_shipping_handling ? '$' + r.internal_shipping_handling : '—' }}</div>
                    <div><span class="combined-cell-label">{{ $t('columns.total') }}:</span> {{ r.total_amount }}</div>
                    <div class="combined-cell-divider"></div>
                    <div><span class="combined-cell-label">{{ $t('columns.qty') }}:</span> {{ r.quantity }}</div>
                    <div><span class="combined-cell-label">{{ $t('columns.size') }}:</span> {{ r.size }}</div>
                  </div>
                </td>
                <td class="oc-cell hover-container" style="min-width: 150px">
                  <view-order-dates :orderData="r" stack-time />
                  <span
                    v-if="r.payment_status"
                    class="oc-payment-chip"
                    :style="{ background: paymentStatusColor(r.payment_status, r.invoice_edit_count).bg, color: paymentStatusColor(r.payment_status, r.invoice_edit_count).color }"
                  >{{ paymentStatusLabel(r.payment_status) }}</span>
                </td>
                <td class="oc-cell oc-actions-cell">
                  <div class="oc-actions-grid">
                    <a
                      v-if="$_has_perm('view_order_detail')"
                      class="oc-action-btn"
                      :tooltip="$t('orders.viewDetails')"
                      @click="initDetail('modal_showApp', r.order_id)"
                    >
                      <i class="material-icons" style="color: #01579b">aspect_ratio</i>
                    </a>
                    <a
                      v-if="$_has_perm('view_order_shipping_invoice') && r.order_invoice_id_fk"
                      class="oc-action-btn"
                      :tooltip="$t('orders.viewShippingInvoiceTooltip')"
                      @click="viewOrderShippingInvoice(r)"
                    >
                      <i class="material-icons" style="color: #00695c">receipt</i>
                    </a>
                    <a
                      v-if="$_has_perm('send_to_customer')"
                      class="oc-action-btn"
                      :tooltip="$t('orders.sendToCustomerTooltip')"
                      @click="deliverToUser(r)"
                    >
                      <i class="material-icons" style="color: #ffb300">local_taxi</i>
                    </a>
                    <a
                      v-if="$_has_perm('return_branch_delivered_order_to_purchased_order')"
                      class="oc-action-btn"
                      :tooltip="$t('orders.returnToPurchaseTooltip')"
                      @click="returnToPurchase(r.order_id)"
                    >
                      <i class="material-icons" style="color: #eb8545">settings_backup_restore</i>
                    </a>
                    <a
                      v-if="$_has_perm('cancel_branch_delivered_order')"
                      class="oc-action-btn"
                      :tooltip="$t('orders.cancelThisOrder')"
                      @click="openCancelingOrderModal(r.order_id, r.total_amount)"
                    >
                      <i class="material-icons" style="color: #c62828">cancel</i>
                    </a>
                    <span v-if="$_has_perm('edit_charged_order') && r.confirm_date" class="oc-action-btn">
                      <edit-order-charge
                        :r="r"
                        :getResults="getResults"
                      ></edit-order-charge>
                    </span>
                    <a
                      v-if="$_has_perm('process_order')"
                      class="oc-action-btn"
                      :tooltip="$t('orders.processOrderTooltip')"
                      @click="openProcessOrderModal(r)"
                    >
                      <i class="material-icons" style="color: #424242">settings</i>
                    </a>
                  </div>
                </td>
              </tr>
              <tr id="no-result-row">
                <td
                  :colspan="columns.length"
                  class="text-center"
                >
                  <v-alert
                    type="error"
                    :value="alertValue"
                  > {{ $t('common.noResultsFound') }} </v-alert>
                </td>
              </tr>
            </tbody>
          </datatable>
          </div>

          <div v-if="isMobile">
            <label
              v-if="apiResults.length > 0"
              class="moc-select-all-bar"
            >
              <input
                type="checkbox"
                v-model="allMobileSelected"
              />
              {{ $t('common.selectAllLabel') }} ({{ qrCodeValues.length }}/{{ apiResults.length }})
            </label>
            <div
              v-for="(r, index) in apiResults"
              :key="r.order_id"
              class="moc-container"
            >
              <div class="moc-card">
                <div class="moc-header" :style="qrCodeValues.find(x => x[0] == r.order_id) ? 'border: 2px solid #FBC02D;' : ''">
                  <input
                    :id="'cb' + index"
                    type="checkbox"
                    :value="[
                      r.order_id,
                      r.full_name,
                      r.phone_number,
                      r.user_id,
                      r.theOrderID,
                      r.is_ship_only,
                      r.credit,
                      r.product_name,
                      r.warehouse_id_fk,
                      r.transportation_type,
                      r.is_ship_only,
                      r.total_amount,
                      r.local_delivery,
                      r.note,
                      r.size,
                      r.payment_status,
                      r.order_invoice_number,
                    ]"
                    v-model="qrCodeValues"
                    class="rtl-mr-8"
                  />
                  <span class="moc-item-code">{{ r.theOrderID }}<span v-if="r.track_number" style="font-size:10px; color:#555; font-weight:400;"> · {{ r.track_number }}</span></span>
                  <span class="moc-status-badge" :style="{ background: '#685AFF', color: '#FFFFFF' }">{{ $_status_label(r.status) }}</span>
                  <span
                    v-if="r.cleared_at"
                    class="moc-status-badge"
                    style="background-color:#FFF9C4; color:#7A6A00;"
                  >cleared</span>
                </div>
                <div class="moc-body">
                  <!-- User -->
                  <div class="moc-row">
                    <span class="moc-label">{{ $t('common.user') }}</span>
                    <span class="moc-value"><view-order-users :orderData="r" /></span>
                  </div>
                  <!-- QR -->
                  <div class="moc-row" v-if="r.qrcode">
                    <span class="moc-label">{{ $t('columns.qr') }}</span>
                    <span class="moc-value"><i class="fa fa-qrcode" aria-hidden="true"></i> {{ r.qrcode }}</span>
                  </div>
                  <div class="moc-row moc-row--2col">
                    <div>
                      <span class="moc-label">{{ $t('columns.invoiceNo') }}</span>
                      <span class="moc-value">{{ r.last_order_invoice || '—' }}</span>
                    </div>
                    <div>
                      <span class="moc-label">{{ $t('columns.customerId') }}</span>
                      <span class="moc-value">{{ r.customer_id_fk || '—' }}</span>
                    </div>
                  </div>
                  <div class="moc-row moc-row--2col">
                    <span class="moc-label">{{ $t('columns.phone') }}</span>
                    <span class="moc-value">{{ r.phone_number || '—' }}</span>
                  </div>
                  <div class="moc-row moc-row--2col">
                    <span class="moc-label">{{ $t('columns.customer') }}</span>
                    <span class="moc-value">{{ r.full_name }}</span>
                  </div>
                  <div class="moc-row moc-row--2col">
                    <span class="moc-label">{{ $t('columns.item') }}</span>
                    <span class="moc-value">{{ r.product_name ? r.product_name.substr(0, 35) + (r.product_name.length > 35 ? '...' : '') : '—' }}</span>
                  </div>
                  <div class="moc-row moc-row--2col" v-if="r.brand">
                    <span class="moc-label">{{ $t('columns.brand') }}</span>
                    <span class="moc-value">{{ r.brand }}</span>
                  </div>
                  <div class="moc-row moc-row--2col" v-if="r.product_url">
                    <span class="moc-label">{{ $t('columns.url') }}</span>
                    <span class="moc-value">
                      <a :href="r.product_url" target="_blank" rel="noopener noreferrer" style="color:#01579B;">
                        <i class="material-icons" style="font-size:20px; vertical-align:middle;">link</i>
                      </a>
                    </span>
                  </div>
                  <div class="moc-row" v-if="r.image">
                    <span class="moc-label">{{ $t('columns.image') }}</span>
                    <span class="moc-value">
                      <img
                        :src="r.image.startsWith('http') ? r.image : `${$_image_path()}${r.image}`"
                        style="width:80px; height:80px; object-fit:cover; border-radius:4px; cursor:pointer;"
                        @click="lightboxImage = r.image.startsWith('http') ? r.image : `${$_image_path()}${r.image}`; lightboxOpen = true"
                      />
                    </span>
                  </div>
                  <div class="moc-row">
                    <span class="moc-label">{{ $t('columns.dateTime') }}</span>
                    <span class="moc-value"><view-order-dates :orderData="r" /></span>
                  </div>
                  <div class="moc-row moc-row--2col">
                    <div>
                      <span class="moc-label">{{ $t('columns.warehouse') }}</span>
                      <span class="moc-value">{{ r.country || '—' }}</span>
                    </div>
                    <div>
                      <span class="moc-label">{{ $t('columns.type') }}</span>
                      <span class="moc-value">{{ r.transportation_type_obj ? r.transportation_type_obj.name.replace('Transportation', '') : '—' }}</span>
                    </div>
                  </div>
                  <div class="moc-row moc-row--2col">
                    <div>
                      <span class="moc-label">{{ $t('columns.price') }}</span>
                      <span class="moc-value">{{ formatPriceInclIntShipping(r) }}</span>
                    </div>
                    <div>
                      <span class="moc-label">{{ $t('columns.total') }}</span>
                      <span class="moc-value">{{ r.total_amount ? '$' + r.total_amount : '—' }}</span>
                    </div>
                  </div>
                  <div class="moc-row moc-row--2col" v-if="r.size">
                    <span class="moc-label">{{ $t('columns.size') }}</span>
                    <span class="moc-value">{{ r.size }}</span>
                  </div>
                  <div class="moc-row moc-row--2col">
                    <span class="moc-label">{{ $t('columns.shipOnly') }}</span>
                    <span class="moc-value">{{ r.is_ship_only == 1 ? $t('common.yes') : $t('common.no') }}</span>
                  </div>
                  <div v-if="r.purchase_note" class="moc-row moc-row--2col">
                    <span class="moc-label">{{ $t('columns.purchaseNote') }}</span>
                    <span class="moc-value">{{ r.purchase_note }}</span>
                  </div>
                  <div class="moc-row moc-row--2col">
                    <span class="moc-label">{{ $t('sidebar.delivery') }}</span>
                    <span class="moc-value">
                      <span v-if="r.delivery_confirmed && r.delivery_type === 'office delivery'" style="background:#FFF3E0; color:#e65100; padding:2px 8px; border-radius:12px; font-size:12px;">{{ $t('delivery.pickupLabel') }}</span>
                      <span v-else-if="r.delivery_type === 'home delivery'" style="background:#E3F2FD; color:#01579B; padding:2px 8px; border-radius:12px; font-size:12px;">{{ $t('orders.homeDelivery') }}</span>
                      <span v-else style="color:#bbb;">{{ $t('delivery.notConfirmedLabel') }}</span>
                    </span>
                  </div>
                  <div class="moc-row moc-row--2col" v-if="r.payment_status">
                    <span class="moc-label">{{ $t('columns.payment') }}</span>
                    <span class="moc-value">
                      <span :style="{ background: paymentStatusColor(r.payment_status, r.invoice_edit_count).bg, color: paymentStatusColor(r.payment_status, r.invoice_edit_count).color, padding: '2px 8px', borderRadius: '12px', fontSize: '12px' }">{{ paymentStatusLabel(r.payment_status) }}</span>
                    </span>
                  </div>
                </div>
                <div class="moc-actions">
                  <a v-if="$_has_perm('view_order_detail')" style="color: #01579b" :tooltip="$t('orders.viewDetails')" @click="initDetail('modal_showApp', r.order_id)">
                    <i class="material-icons">aspect_ratio</i>
                  </a>
                  <a v-if="$_has_perm('view_order_shipping_invoice') && r.order_invoice_id_fk" style="color: #00695c" :tooltip="$t('orders.viewShippingInvoiceTooltip')" @click="viewOrderShippingInvoice(r)">
                    <i class="material-icons">receipt</i>
                  </a>
                  <a v-if="$_has_perm('send_to_customer')" style="color: #ffb300" :tooltip="$t('orders.sendToCustomerTooltip')" @click="deliverToUser(r)">
                    <i class="material-icons">local_taxi</i>
                  </a>
                  <a v-if="$_has_perm('return_branch_delivered_order_to_purchased_order')" style="color: #eb8545" :tooltip="$t('orders.returnToPurchaseTooltip')" @click="returnToPurchase(r.order_id)">
                    <i class="material-icons">settings_backup_restore</i>
                  </a>
                  <a v-if="$_has_perm('cancel_branch_delivered_order')" style="color: #c62828" :tooltip="$t('orders.cancelThisOrder')" @click="openCancelingOrderModal(r.order_id, r.total_amount)">
                    <i class="material-icons">cancel</i>
                  </a>
                  <edit-order-charge
                    v-if="$_has_perm('edit_charged_order') && r.confirm_date"
                    :r="r"
                    :getResults="getResults"
                  ></edit-order-charge>
                  <a
                    v-if="$_has_perm('process_order')"
                    style="color: #424242"
                    :tooltip="$t('orders.processOrderTooltip')"
                    @click="openProcessOrderModal(r)"
                  >
                    <i class="material-icons">settings</i>
                  </a>
                </div>
              </div>
            </div>
            <v-alert type="error" :value="alertValue"> {{ $t('common.noResultsFound') }} </v-alert>
          </div>

          <v-dialog v-model="lightboxOpen" max-width="600px">
            <v-card>
              <v-card-actions><v-spacer></v-spacer><v-btn icon @click="lightboxOpen=false"><v-icon>close</v-icon></v-btn></v-card-actions>
              <v-card-text style="text-align:center; padding-top:0">
                <img :src="lightboxImage" style="max-width:100%;max-height:80vh;" />
              </v-card-text>
            </v-card>
          </v-dialog>

          <view-order-details
            :detialData="detialData"
            :closeModal="closeModal"
          ></view-order-details>

          <create-qr-code
            :openGenerateQrCodeModal="openGenerateQrCodeModal"
            :generateQrCodeModal="generateQrCodeModal"
            :closeGenerateQrCodeModal="closeGenerateQrCodeModal"
            :qrCodeValues="qrCodeValues"
            :generateQrCode="generateQrCode"
            :getResults="getResults"
            :priceIndex="11"
          >
          </create-qr-code>

          <process-the-order
            v-if="currentProcessOrder"
            ref="processComp"
            :processOrder="processOrder"
            :r="currentProcessOrder"
            :checkAlert="checkAlert"
            :updateData="updateData"
            :calculateTotalAmount="calculateTotalAmount"
            :loading="loading"
            :getResults="getResults"
            :clearProcessOrderData="clearProcessOrderData"
            :theQuantity="theQuantity"
            :itemTypes="itemTypes"
            :warehouses="warehouses"
            :reasons="reasons"
          ></process-the-order>

          <send-to-user
            :closeSendToUserModal="closeSendToUserModal"
            :sendToUserModal="sendToUserModal"
            :sendToUserData="sendToUserData"
            :sendToUser="sendToUser"
          >
          </send-to-user>

          <prepare-to-print
            :closeModal="closeModal"
            :printData="printData"
          ></prepare-to-print>

          <add-order-invoice
            ref="addOrderInvoice"
            :formData="generateInvoiceData"
            :orderInvoiceModal="orderInvoiceModal"
            @close="orderInvoiceModal = false, qrCodeValues = []"
            @invoice-created="onInvoiceCreated()"
            @view-clicked="onInvoiceViewed()"
          />

          <add-delivery-invoice
            :formData="deliveryInvoiceData"
            :deliveryInvoiceModal="deliveryInvoiceModal"
            @close="deliveryInvoiceModal = false"
            @invoice-created="viewDeliveryInvoice"
          />

          <customer-delivery-summary
            :summaryModal="customerDeliverySummaryModal"
            :apiResults="apiResults"
            :fetchUrl="'api/branch_delivery_order'"
            :queryParams="tableData"
            @close="customerDeliverySummaryModal = false"
            @cleared="getResults()"
          ></customer-delivery-summary>

          <view-customer-delivery-invoice
            :invoiceData="invoiceData"
            :companyData="companyData"
            :closeModal="closeModal"
          />

          <admin-cancel-order
            :closeCancelingOrderModal="closeCancelingOrderModal"
            :cancelOrderModal="cancelOrderModal"
            :cancelAlert="cancelAlert"
            :cancelData="cancelData"
            :cancelingOrder="cancelingOrder"
            :reasons="reasons"
            :willAutoRefund="true"
            :refundAmount="cancelRefundAmount"
          ></admin-cancel-order>

          <div class="row">
            <div class="col-xs-12 text-center oc-pagination">
              <paginate
                :page-count="pagination.lastPage"
                :page-range="2"
                :changePage="changePage"
                :container-class="'pagination'"
                :pagination="pagination"
                :page-class="'page-item'"
              >
              </paginate>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PaginationVue from './Pagination.vue'
import preDatatableComponent from './preDatatableComponent.vue'
import DatatableVue from './Datatable.vue'
import viewOrderDetails from './viewOrderDetails.vue'
import sendToUser from './sendToUser.vue'
import prepareToPrint from './prepareToPrint'
import axios from 'axios'
import swal from 'sweetalert'
import createQrCode from './createQrCode.vue'
import AddOrderInvoice from '../components/orderInvoice/AddOrderInvoice.vue'
import AddDeliveryInvoice from '../components/deliveryInvoice/AddDeliveryInvoice.vue'
import ViewCustomerDeliveryInvoice from './deliveryInvoice/ViewCustomerDeliveryInvoice.vue'
import customerDeliverySummary from './customerDeliverySummary.vue'
import editOrderCharge from './editOrderCharge.vue'
import processTheOrder from './processTheOrder.vue'
import adminCancelOrder from './adminCancelOrder'
import statusHelpers from '../mixins/statusHelpers'
import toolbarButtonDisplay from '../mixins/toolbarButtonDisplay'

export default {
  mixins: [statusHelpers, toolbarButtonDisplay],
  components: {
    preDatatableComponent: preDatatableComponent,
    datatable: DatatableVue,
    paginate: PaginationVue,
    'view-order-details': viewOrderDetails,
    'send-to-user': sendToUser,
    'prepare-to-print': prepareToPrint,
    'create-qr-code': createQrCode,
    AddOrderInvoice,
    AddDeliveryInvoice,
    ViewCustomerDeliveryInvoice,
    'customer-delivery-summary': customerDeliverySummary,
    'edit-order-charge': editOrderCharge,
    'process-the-order': processTheOrder,
    'admin-cancel-order': adminCancelOrder
  },
  mounted() {
    this.$_fetch_statuses()
    this.getWarehouses()
    this.perPageFix()
    this.getCustomer()
    this.getItemType()
    this.getReasons()

    if (screen.width <= 600) {
      this.isMobile = true
    } else {
      this.isMobile = false
    }
  },
  $_veeValidate: {
    validator: 'new',
  },
  data() {
    let sortOrders = {}
    // Grouped columns (redesign, mirrors allOrder.vue's pattern) -- was a flat ~25-column
    // list; each grouped cell stacks several related fields (QR / track # / invoice # /
    // delivered-by user fold into Order Info; ship-only, cleared, delivery method &
    // purchase note into Status & Location; payment chip into Date & Payment). Per-column
    // hide now only makes sense at the group level, so defaultHideCol is emptied and
    // storageKey bumped so stale hidden-column selections don't misapply to new positions.
    let columns = [
      { label: '', name: '', text: this.$t('columns.checkBoxColumn'), value: '1' },
      { label: this.$t('columns.orderInfo'), text: this.$t('columns.orderInfo'), value: '2', sortable: true, searchable: true, name: 'theOrderID' },
      { label: this.$t('columns.customer'), text: this.$t('columns.customer'), value: '3', sortable: true, searchable: true, name: 'full_name' },
      { label: this.$t('columns.itemDetails'), text: this.$t('columns.itemDetails'), value: '4', sortable: true, searchable: true, name: 'product_name' },
      { label: this.$t('columns.product'), text: this.$t('columns.product'), value: '5', sortable: false, searchable: false, name: 'image' },
      { label: this.$t('columns.statusLocation'), text: this.$t('columns.statusLocation'), value: '6', sortable: true, searchable: true, name: 'status' },
      { label: this.$t('columns.pricing'), text: this.$t('columns.pricing'), value: '7', sortable: false, searchable: false, name: 'price_all' },
      { label: this.$t('columns.datePayment'), text: this.$t('columns.datePayment'), value: '8', sortable: true, searchable: false, name: 'updated_at' },
      { label: this.$t('columns.actions'), text: this.$t('columns.actions'), value: '9', sortable: false, searchable: false, name: 'actions' }
    ]
    columns.forEach((column) => {
      sortOrders[column.name] = -1
    })

    return {
      isMobile: false,
      loading: false,
      // Grouped cells can't be hidden field-by-field any more -- same tradeoff allOrder.vue made.
      defaultHideCol: [],
      // Hidden by default (per-page preference, same localStorage convention as allOrder.vue).
      showFilters: localStorage.getItem('filtersVisible_branchDeliveredOrder') === 'true',
      url: 'api/branch_delivery_order',
      archiveOrderUrl: 'api/user_delivering',
      orderDetailUrl: 'api/show_order_detail',
      returnToPurchaseUrl: 'api/orders/',
      prepareToPrintUrl: 'api/prepare_to_print',
      getCustomerPrice: 'api/subscriptions/customer-price',
      processOrderUrl: 'api/orders',
      getItemTypeUrl: 'api/get-item-type',
      cancelingOrderUrl: 'api/canceling_order_by_admin',
      sendToUserModal: false,
      alertValue: false,
      checkAlert: false,
      btnsave: true,
      customersList: [],
      reasons: [],
      cancelOrderModal: false,
      cancelAlert: false,
      theCancelOrderID: '',
      cancelRefundAmount: null,
      cancelData: {
        cancel_reason: ''
      },
      orderInvoiceModal: false,
      generateInvoiceData: {},
      deliveryInvoiceData: {},
      deliveryInvoiceModal: false,
      customerDeliverySummaryModal: false,
      companyData: {},
      invoiceData: {},
      generateQrCodeModal: false,
      qrCodeValues: [],
      filterMade: false,
      totalResult: 0,
      resultsVersion: 0,
      queryParameter: true,
      sendToUserData: {
        order_id: '',
      },
      // Process Order (single instance pattern, mirrors allOrder.vue)
      currentProcessOrder: null,
      theQuantity: { quantity: '' },
      itemTypeResult: [],
      itemTypes: [],
      updateData: {
        item_price: null,
        the_tl_price: null,
        the_aed_price: null,
        the_cny_price: null,
        the_gbp_price: null,
        the_eur_price: null,
        tax: null,
        national_tax: null,
        item_weight: null,
        internal_shipping_handling: null,
        delivery_price: null,
        kreenex_fee: null,
        quantity_charge: null,
        insurance: null,
        total_amount: null,
        from_date: null,
        to_date: null,
        description: '',
        tmp_shipping: null,
        item_weight_lb: null,
        price_weight: null,
        send_sms: false,
        discount: null,
        item_type: '',
        transportation_type: null,
        is_repackage: false,
        is_hold: false,
        other_service: '',
        local_delivery: 0,
        process_note: '',
        customer_process_note: '',
        repackage_price: null,
        hold_price: null,
        other_service_price: null,
        customer_id_fk: null,
        product_name: null,
        product_url: null,
        quantity: null,
        color: null,
        size: null,
        note: null,
        brand: null
      },
      detialData: {},
      prevSearchQuery: '',
      warehouses: [],
      lightboxImage: null,
      lightboxOpen: false,
      apiResults: [],
      detailResults: [],
      orderID: {
        orders_id: [],
      },
      printData: [],
      columns: columns,
      sortKey: 'updated_at',
      sortOrders: sortOrders,
      clearedFilterVal: 'all',
      clearedFilterItems: [
        { text: this.$t('common.active'), value: 'active' },
        { text: this.$t('columns.cleared'), value: 'cleared' },
        { text: this.$t('common.all'), value: 'all' },
      ],
      tableData: {
        per_page: 10,
        search: '',
        column: 'updated_at',
        sortDir: 'desc',
        searchOneCol: '',
        searchOneVal: '',
        from_date: '',
        to_date: '',
        warehouse_id_fk: '',
        branches_id: '',
        cleared_filter: 'all',
      },
      pagination: {
        lastPage: 1,
        currentPage: 1,
        total: 1,
        lastPageUrl: '',
        nextPageUrl: '',
        prevPageUrl: '',
        from: '',
        to: '',
      },
    }
  },

  computed: {
    summaryTotalOrders() {
      if (this.qrCodeValues.length > 0) return this.qrCodeValues.length
      return this.filterMade
        ? this.apiResults.length
        : (this.pagination.total || this.apiResults.length)
    },
    summaryTotalAmount() {
      if (this.qrCodeValues.length > 0) {
        return this.qrCodeValues.reduce((sum, v) => sum + (Number(v[11]) || 0), 0)
      }
      return this.apiResults.reduce((sum, r) => sum + (Number(r.total_amount) || 0), 0)
    },
    allMobileSelected: {
      get() {
        return this.apiResults.length > 0 && this.qrCodeValues.length === this.apiResults.length
      },
      set(value) {
        if (value) {
          this.checkAll()
        } else {
          this.unCheckAll()
        }
      },
    },
    // Best-effort only: counts within the currently loaded page of apiResults, not a true
    // system-wide count -- hence the "(current page)" note shown alongside it in the stat card.
    ordersTodayCount() {
      const today = new Date().toISOString().split('T')[0]
      return this.apiResults.filter(r => {
        const d = r.branch_delivery_date || r.updated_at || r.order_date || r.created_at
        return d && d.substr(0, 10) === today
      }).length
    },
    // Same current-page-only caveat as ordersTodayCount above.
    pendingPaymentsCount() {
      return this.apiResults.filter(r => r.payment_status && r.payment_status !== 'paid').length
    },
  },

  watch: {
    clearedFilterVal(newVal) {
      this.handleClearedFilter(newVal)
    },
  },

  methods: {
    toggleFiltersVisible() {
      this.showFilters = !this.showFilters
      localStorage.setItem('filtersVisible_branchDeliveredOrder', this.showFilters ? 'true' : 'false')
    },

    // Replicates preDatatableComponent.vue's own built-in Reload button (now relocated
    // into the panel-heading here) -- reaches into the child's own filter-chip/date-range
    // reset state via $refs since that state lives inside the shared component, not here.
    reloadTable() {
      this.resetGetResults()
      const preDatatable = this.$refs.preDatatable
      if (preDatatable) {
        preDatatable.filterApplied = false
        preDatatable.resetDatePickerDefaults()
        preDatatable.ware = 'Select'
      }
    },

    formatPriceInclIntShipping(r) {
      const p = Number(r.item_price) || 0
      const s = Number(r.internal_shipping_handling) || 0
      const n = p + s
      return n ? '$' + n.toFixed(2) : '—'
    },

    paymentStatusColor(status, editCount) {
      // Auto-generated invoices (created automatically at order approval, never yet touched by
      // staff via Generate Invoice — see edit_count) show grey instead of the urgent red used for
      // a genuinely unpaid invoice, since there's nothing to chase yet. Matches
      // orderInvoices.vue's statusColor() convention.
      if (status === 'not_paid' && !Number(editCount)) {
        return { bg: '#E0E0E0', color: '#616161' }
      }

      const map = {
        not_paid: { bg: '#F63049', color: '#FFFFFF' },
        partial_paid: { bg: '#FFC85C', color: '#6B4C00' },
        paid: { bg: '#89D7B7', color: '#1A5940' },
        cancel: { bg: '#FB8C00', color: '#FFFFFF' }
      }
      return map[status] || { bg: '#F5F5F5', color: '#616161' }
    },

    paymentStatusLabel(status) {
      if (!status) return ''
      return status.replace(/_/g, ' ')
    },

    getCompanyData(token) {
      axios
        .get('api/company_info', { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          this.companyData = response.data.data
        })
    },

    viewDeliveryInvoice(data) {
      let token = localStorage.getItem('access_token')
      this.getCompanyData(token)

      axios
        .get(`api/delivery_invoice/${data.delivery_invoice_id}`, {
          headers: { Authorization: 'Bearer ' + token }
        })
        .then((res) => {
          this.invoiceData = res.data
          this.modal('view_customer_delivery_invoice_modal')
        })
    },

    branchEdit(branches) {
      this.tableData.branches_id = branches.toString()
      this.getResults()
    },

    // Scoped to #datatable (this page's own <datatable> table) and deterministic set/unset —
    // the old unscoped $('td:nth-child(N)') selector matched EVERY table on the page, including
    // the Generate/View Invoice modal's items-table (also 7 columns), silently hiding its Total
    // cell whenever this page's own column visibility got applied. See orderInvoices.vue's
    // showHideColumn for the same fix.
    showHideColumn(prevColVal, currentColVal) {
      const hidden = {}
      currentColVal.forEach((v) => { hidden[v] = true })

      for (let i = 1; i <= this.columns.length; i++) {
        const selector = '#datatable td:nth-child(' + i + '),#datatable th:nth-child(' + i + ')'
        if (hidden[i]) {
          $(selector).hide()
        } else {
          $(selector).show()
        }
      }
    },

    getCustomer() {
      let token = localStorage.getItem('access_token')

      axios
        .get('api/get-customer-to-select', {
          headers: { Authorization: 'Bearer ' + token },
        })
        .then((response) => {
          this.customersList = []
          let result = response.data.data

          result.forEach((c) => {
            this.customersList.push({
              text: c.full_name + ' - Tel: ' + [c.phone_number, c.phone_number_2].filter(Boolean).join(' / '),
              value: c.user_id,
              phone: c.phone_number,
            })
          })
        })
    },

    checkDiff(arr) {
      return arr.length !== 0 && new Set(arr).size !== 1
    },

    // Source button is commented out in the template for now (kept for later use).
    openDeliveryInvoiceModal() {
      if (this.qrCodeValues.length < 1) {
        swal({
          text: this.$t('orders.pleaseSelectOrderOrCustomer'),
          icon: 'warning',
          dangerMode: true,
        })

        return
      }

      let customerIds = []
      let ordersArr = []
      this.qrCodeValues.forEach((x, i) => {
        let obj = {
          order_id: null,
          customer_name: null,
          phone_number: null,
          customer_credit: null,
          customer_id: null,
          the_order_id: null
        }
        x.forEach((y, j) => {
          if (j === 0) {
            obj.order_id = y
          } else if (j === 1) {
            obj.customer_name = y
          } else if (j === 2) {
            obj.phone_number = y
          } else if (j === 3) {
            customerIds.push(y)
            obj.customer_id = y
          } else if (j === 4) {
            obj.the_order_id = y
          } else if (j === 8) {
            obj.customer_credit = Number(y)
          }
        })
        ordersArr.push(obj)
      })

      if (this.checkDiff(customerIds)) {
        swal({
          text: this.$t('orders.selectedDifferentCustomers'),
          icon: 'warning',
          dangerMode: true,
        })

        return
      }

      this.deliveryInvoiceData = {
        customer_id_fk: ordersArr[0].customer_id,
        customer_name: ordersArr[0].customer_name,
        phone_number: ordersArr[0].phone_number,
        customer_credit: ordersArr[0].customer_credit,
        orders: ordersArr,
        orders_id: ordersArr.map((order) => order.order_id),
      }

      this.deliveryInvoiceModal = true
    },

    openOrderInvoiceModal() {
      if (this.qrCodeValues.length < 1) {
        swal({
          text: this.$t('orders.pleaseSelectOrderOrCustomer'),
          icon: 'warning',
          dangerMode: true,
        })

        return
      }

      let customerIds = []
      let ordersArr = []
      this.qrCodeValues.forEach((x, i) => {
        let obj = {
          order_id: null,
          customer_name: null,
          phone: null,
          customer_id: null,
          the_order_id: null,
          warehouse_id: null,
          transportation_type: null,
          is_ship_only: null,
          total_amount: null,
          local_delivery: 0,
          note: null,
          product_name: null,
          size: null,
          payment_status: null,
          order_invoice_number: null
        }
        x.forEach((y, j) => {
          if (j === 0) {
            obj.order_id = y
          } else if (j === 1) {
            obj.customer_name = y
          } else if (j === 2) {
            obj.phone = y
          } else if (j === 3) {
            customerIds.push(y)
            obj.customer_id = y
          } else if (j === 4) {
            obj.the_order_id = y
          } else if (j === 7) {
            obj.product_name = y
          } else if (j === 8) {
            obj.warehouse_id = y
          } else if (j === 9) {
            obj.transportation_type = y
          } else if (j === 10) {
            obj.is_ship_only = y
          } else if (j === 11) {
            obj.total_amount = y
          } else if (j === 12) {
            obj.local_delivery = Number(y) || 0
          } else if (j === 13) {
            obj.note = y
          } else if (j === 14) {
            obj.size = y
          } else if (j === 15) {
            obj.payment_status = y
          } else if (j === 16) {
            obj.order_invoice_number = y
          }
        })
        ordersArr.push(obj)
      })

      if (this.checkDiff(customerIds)) {
        swal({
          text: this.$t('orders.selectedDifferentCustomers'),
          icon: 'warning',
          dangerMode: true,
        })

        return
      }

      const totalLocalDelivery = ordersArr.reduce((sum, o) => sum + (Number(o.local_delivery) || 0), 0)
      const combinedNote = ordersArr.map((o) => o.note).filter(Boolean).join(' | ')

      const openModal = () => {
        this.generateInvoiceData = {
          volume: 0,
          delivery_price: 0,
          local_delivery: totalLocalDelivery,
          note: combinedNote,
          customer_id: ordersArr[0].customer_id,
          customer_name: ordersArr[0].customer_name,
          warehouse_id: ordersArr[0].warehouse_id,
          transportation_type: ordersArr[0].transportation_type,
          is_ship_only: ordersArr[0].is_ship_only,
          phone: ordersArr[0].phone,
          orders: ordersArr,
        }

        this.orderInvoiceModal = true
      }

      // Orders can already sit on a paid/partially-paid invoice — Generate Invoice on them again
      // no longer double-charges the customer (server-side, updateExistingInvoice() caps any new
      // payment to what's actually still owed), but staff should still be told up front rather
      // than being left to assume this is fresh, unpaid work. Mirrors allOrder.vue's
      // openOrderInvoiceFromHeader()/openOrderInvoiceModal() warning.
      const alreadySettled = ordersArr.filter((o) => o.payment_status === 'paid' || o.payment_status === 'partial_paid')

      if (alreadySettled.length > 0) {
        const lines = alreadySettled
          .map((o) => `${o.the_order_id} — ${this.paymentStatusLabel(o.payment_status)}${o.order_invoice_number ? ' (invoice #' + o.order_invoice_number + ')' : ''}`)
          .join('\n')

        swal({
          title: this.$t('orders.someOrdersAlreadyInvoiced'),
          text: this.$t('changeOrderStatus.ordersAlreadyHaveInvoiceMessage', { lines }),
          icon: 'warning',
          buttons: true,
          dangerMode: true,
        }).then((confirmed) => {
          if (confirmed) openModal()
        })

        return
      }

      openModal()
    },

    // Source button is commented out in the template for now (kept for later use).
    attachSelectedToDelivery() {
      if (this.qrCodeValues.length < 1) {
        swal({
          text: this.$t('orders.pleaseSelectOrderOrCustomer'),
          icon: 'warning',
          dangerMode: true,
        })

        return
      }

      const customerIds = this.qrCodeValues.map((x) => x[3])
      if (this.checkDiff(customerIds)) {
        swal({
          text: this.$t('orders.selectedDifferentCustomers'),
          icon: 'warning',
          dangerMode: true,
        })

        return
      }

      const customerId = customerIds[0]
      const orderIds = this.qrCodeValues.map((x) => x[0])

      swal({
        title: this.$t('common.areYouSure'),
        text: this.$t('orders.confirmAttachToDeliveryRequest'),
        icon: 'warning',
        buttons: true,
      }).then((willAttach) => {
        if (!willAttach) return

        let token = localStorage.getItem('access_token')
        axios
          .post('api/order-delivery/attach-orders', {
            customer_id: customerId,
            order_ids: orderIds,
          }, { headers: { Authorization: 'Bearer ' + token } })
          .then((response) => {
            this.qrCodeValues = []
            this.getResults()
            swal({ text: response.data.message, icon: 'success' })
          })
          .catch((err) => {
            const msg = (err.response && err.response.data && err.response.data.message)
              ? err.response.data.message
              : 'Something went wrong!'
            swal({ text: msg, icon: 'error' })
          })
      })
    },

    generateInvoiceCustomer(id) {
      const customer = this.customersList.find((x) => x.value === id)
      this.generateInvoiceData = {
        customer_id: customer.value,
        customer_name: customer.text,
        phone: customer.phone,
        local_delivery: 0,
        orders: [],
      }

      this.orderInvoiceModal = true
    },

    openCustomerDeliverySummaryModal() {
      this.customerDeliverySummaryModal = true
    },

    confirmUnclearAll() {
      if (this.qrCodeValues.length < 1) {
        swal({
          text: this.$t('orders.pleaseAtLeastSelectOneOrder'),
          icon: 'warning',
          dangerMode: true,
        })

        return
      }

      const orderIds = this.qrCodeValues.map((v) => v[0])

      swal({
        title: this.$t('common.areYouSure'),
        text: this.$t('changeOrderStatus.unmarkOrdersAsClearedConfirm', { count: orderIds.length }),
        icon: 'warning',
        buttons: true,
      }).then((confirm) => {
        if (!confirm) return

        const token = localStorage.getItem('access_token')

        axios
          .post('api/orders/unmark-cleared', { orders: orderIds }, {
            headers: { Authorization: 'Bearer ' + token },
          })
          .then(() => {
            this.qrCodeValues = []
            this.getResults()
          })
          .catch((error) => {
            const msg = (error.response && error.response.data && error.response.data.message) || this.$t('orders.failedToUnclearOrders')
            swal(this.$t('common.error'), msg, 'error')
          })
      })
    },

    openGenerateQrCodeModal() {
      if (this.qrCodeValues.length < 1) {
        swal({
          text: this.$t('orders.pleaseAtLeastSelectOneOrder'),
          icon: 'warning',
          dangerMode: true,
        })
      } else {
        this.generateQrCodeModal = true
      }
    },

    closeGenerateQrCodeModal() {
      this.generateQrCodeModal = false
      this.qrCodeValues = []
      this.getResults()
    },

    generateQrCode() {
      this.closeGenerateQrCodeModal()
    },

    checkAll() {
      this.qrCodeValues = this.apiResults.map(r => [r.order_id, r.full_name, r.phone_number, r.user_id, r.theOrderID, r.is_ship_only, r.credit, r.product_name, r.warehouse_id_fk, r.transportation_type, r.is_ship_only, r.total_amount, r.local_delivery, r.note, r.size, r.payment_status, r.order_invoice_number])
    },

    unCheckAll() {
      this.qrCodeValues = []
    },

    prepareToPrint() {
      let token = localStorage.getItem('access_token')
      axios
        .get(this.prepareToPrintUrl, {
          params: this.orderID,
          headers: { Authorization: 'Bearer ' + token },
        })
        .then((response) => {
          let data = []
          data = response.data.data
          this.printData = []
          this.printData = data
        })
      this.modal('prepare_modal')
    },

    getOrderID(orders) {
      this.orderID.orders_id = []
      for (let i = 0; i < orders.length; i++) {
        this.orderID.orders_id[i] = orders[i].order_id
      }
    },

    onInvoiceCreated() {
      const orders = (this.generateInvoiceData && this.generateInvoiceData.orders) || []
      const orderIds = orders.map(o => o.order_id).filter(Boolean)

      if (orderIds.length === 0) {
        this.getResults()
        return
      }

      let token = localStorage.getItem('access_token')
      axios
        .put('api/user_delivering_multiple', { orders: orderIds }, {
          headers: { Authorization: 'Bearer ' + token }
        })
        .then(response => {
          const skipped = response.data && response.data.skipped_orders
          if (skipped && skipped.length > 0) {
            swal({
              title: this.$t('orders.someOrdersNotMarkedDelivered'),
              text: this.$t('orders.missingQrCodeForMessage', { list: skipped.join(', ') }),
              icon: 'warning'
            })
          }
          this.getResults()
        })
        .catch(err => {
          const msg = (err.response && err.response.data && err.response.data.message)
            ? err.response.data.message
            : this.$t('orders.failedToMarkOrdersAsDelivered')
          swal(this.$t('common.warning'), msg, 'warning')
          this.getResults()
        })
    },

    onInvoiceViewed() {
      const orders = (this.generateInvoiceData && this.generateInvoiceData.orders) || []
      const orderIds = orders.map(o => o.order_id).filter(Boolean)
      if (orderIds.length === 0) return

      let token = localStorage.getItem('access_token')
      axios
        .put('api/user_delivering_multiple', { orders: orderIds }, {
          headers: { Authorization: 'Bearer ' + token }
        })
        .then(response => {
          const skipped = response.data && response.data.skipped_orders
          if (skipped && skipped.length > 0) {
            swal({
              title: this.$t('orders.someOrdersNotMarkedDelivered'),
              text: this.$t('orders.missingQrCodeForMessage', { list: skipped.join(', ') }),
              icon: 'warning'
            })
          }
        })
        .catch(err => {
          const msg = (err.response && err.response.data && err.response.data.message)
            ? err.response.data.message
            : this.$t('orders.failedToMarkOrdersAsDelivered')
          swal(this.$t('common.warning'), msg, 'warning')
        })
    },

    getResults(url = this.url) {
      var token = localStorage.getItem('access_token')
      let theDatatable = document.querySelector('.the-datatable')
      let thePanel = document.querySelector('.panel-flat')

      // A selection made before a filter/search/reload must not silently carry over into
      // the new result set — otherwise a stale row (no longer even visible) stays selected
      // and gets included in the next bulk action, affecting rows the user never intended
      // to touch in the current (filtered) view.
      this.qrCodeValues = []

      if (theDatatable != null) {
        theDatatable.classList.add('slide-table-out')
        thePanel.style.overflowX = 'auto'
      }

      axios
        .get(url, { params: this.tableData, headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          let data = response.data
          const isFlatArray = Array.isArray(data.data)
          if (isFlatArray) {
            this.filterMade = true
            this.apiResults = data.data
          } else {
            this.filterMade = false
            this.apiResults = data.data.data
          }
          this.resultsVersion++

          if (this.apiResults.length === 0) {
            this.tableData.searchOneCol = ''
            this.tableData.searchOneVal = ''
            this.tableData.search = ''
            this.alertValue = true
          } else {
            this.alertValue = false
          }
          if (isFlatArray) {
            this.removeConfigPagination()
          } else {
            this.configPagination(data.data)
          }
          this.getOrderID(this.apiResults)
          if (theDatatable) { theDatatable.classList.remove('slide-table-out') }
          setTimeout(function () {
            thePanel.style.overflowX = 'auto'
          }, 0)
        })
        .then(() => {
          this.totalResult = 0
          for (let i = 0; i < this.apiResults.length; i++) {
            this.totalResult += 1
          }
        })
        .catch((errors) => { })
    },

    sendToExport() {
      let hostName = window.location.hostname
      let protocol = window.location.protocol + '//'
      let fullHostName = protocol + hostName

      // Build URL parameters from current table state
      const params = new URLSearchParams({
        searchedOneCol: this.tableData.searchOneCol || '',
        searchedOneVal: this.tableData.searchOneVal || '',
        column: this.tableData.column || 'updated_at',
        sortDir: this.tableData.sortDir || 'desc',
        searchValue: this.tableData.search || '',
        from_date: this.tableData.from_date || '',
        to_date: this.tableData.to_date || '',
        warehouses: localStorage.getItem('warehouse_id_fk') || '',
        per_page: this.tableData.per_page || 10,
        branches_id: this.tableData.branches_id || ''
      })

      window.location.replace(fullHostName + '/api/delivered-to-erbil-export?' + params.toString())
    },

    removeConfigPagination() {
      this.pagination.lastPage = ''
      this.pagination.currentPage = ''
      this.pagination.total = ''
      this.pagination.lastPageUrl = ''
      this.pagination.nextPageUrl = ''
      this.pagination.prevPageUrl = ''
      this.pagination.from = ''
      this.pagination.to = ''
    },

    resetGetResults() {
      this.queryParameter = false
      this.tableData.searchOneCol = ''
      this.tableData.searchOneVal = ''
      this.tableData.search = ''
      this.sortKey = 'updated_at'
      this.tableData.sortDir = 'desc'
      this.tableData.column = 'updated_at'
      this.tableData.from_date = ''
      this.tableData.to_date = ''
      this.tableData.warehouse_id_fk = ''
      this.tableData.cleared_filter = 'all'
      this.clearedFilterVal = 'all'
      this.getResults()
    },

    sendAllToUser() {
      if (this.qrCodeValues.length < 1) {
        swal({
          text: this.$t('orders.pleaseAtLeastSelectOneOrder'),
          icon: 'warning',
          dangerMode: true,
        })

        return
      }
      let customerIds = []
      let orderIds = []
      this.qrCodeValues.forEach((x, i) => {
        x.forEach((y, j) => {
          if (j === 0) {
            orderIds.push(y)
          } else if (j === 3) {
            customerIds.push(y)
          }
        })
      })

      if (this.checkDiff(customerIds)) {
        swal({
          text: this.$t('orders.selectedDifferentCustomers'),
          icon: 'warning',
          dangerMode: true,
        })

        return
      }

      const data = this.apiResults.find((x) => x.user_id === customerIds[0])

      if (Number(data.credit) < 0) {
        swal({
          title: this.$t('common.warning'),
          text: this.$t('orders.customerHasLoanMustPayMessage', { name: data.full_name, amount: Number(data.credit) * -1 }),
          icon: 'warning',
          buttons: [this.$t('common.cancel'), this.$t('common.skip')],
        }).then((confirm) => {
          if (confirm) {
            let token = localStorage.getItem('access_token')

            axios
              .put(
                'api/user_delivering_multiple',
                { orders: orderIds },
                {
                  headers: { Authorization: 'Bearer ' + token },
                }
              )
              .then(() => {
                this.getResults()
                swal({
                  title: this.$t('common.done'),
                  text: '',
                  icon: 'success',
                  timer: 1000,
                  buttons: false,
                })
              })
              .catch((err) => {
                swal(this.$t('common.warning'), err.response.data.message, 'error')
              })
          }
        })

        return
      }

      swal({
        title: this.$t('common.areYouSure'),
        text: this.$t('orders.onceSentPluralWarning'),
        icon: 'warning',
        buttons: true,
      }).then((confirm) => {
        if (confirm) {
          let token = localStorage.getItem('access_token')

          axios
            .put(
              'api/user_delivering_multiple',
              { orders: orderIds },
              {
                headers: { Authorization: 'Bearer ' + token },
              }
            )
            .then(() => {
              this.getResults()
              swal({
                title: this.$t('common.done'),
                text: '',
                icon: 'success',
                timer: 1000,
                buttons: false,
              })
            })
            .catch((err) => {
              swal(this.$t('common.warning'), err.response.data.message, 'error')
            })
        }
      })
    },

    openSendToUserModal() {
      this.sendToUserModal = true
    },

    closeSendToUserModal() {
      this.sendToUserModal = false
      this.sendToUserData.order_id = ''
    },

    sendToUser() {
      let token = localStorage.getItem('access_token')

      axios
        .get(`api/get_order_detail/${this.sendToUserData.order_id}`, {
          headers: { Authorization: 'Bearer ' + token },
        })
        .then((response) => {
          if (response.data.data.customer.credit < 0) {
            swal(
              this.$t('common.error'),
              this.$t('orders.customerHasLoanMustPayMessage', { name: response.data.data.customer.full_name, amount: response.data.data.customer.credit * -1 }),
              'error'
            )
            return
          }

          axios
            .put(this.archiveOrderUrl + '/' + this.sendToUserData.order_id, '', {
              headers: { Authorization: 'Bearer ' + token },
            })
            .then((response) => {
              this.sendToUserData.order_id = ''
              this.getResults()
            })
            .catch((error) => {
              this.sendToUserData.order_id = ''
              swal(this.$t('common.error'), `${error.response.data.message}`, 'error')
            })
        })
    },

    returnToPurchase(id) {
      swal({
        title: this.$t('common.areYouSure'),
        text: this.$t('orders.confirmReturnToPurchasedSection'),
        icon: 'warning',
        buttons: true,
      }).then((willDelete) => {
        if (willDelete) {
          let token = localStorage.getItem('access_token')
          axios
            .put(this.returnToPurchaseUrl + id + '/return_to_purchase', '', {
              headers: { Authorization: 'Bearer ' + token },
            })
            .then((response) => {
              this.getResults()
              swal({
                title: this.$t('common.done'),
                text: '',
                icon: 'success',
                timer: 1500,
                buttons: false,
              })
            })
            .catch((error) => {
              if (error.response) {
                swal(this.$t('common.error'), `${error.response.data.message}`, 'error')
              }
            })
        }
      })
    },

    resetGetResults2() {
      this.resetGetResults()
    },

    getWarehouses() {
      let token = localStorage.getItem('access_token')
      this.warehouses = []
      axios.get('api/warehouses', { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          response.data.data.forEach((r) => {
            this.warehouses.push({ text: r.country, value: r.warehouse_id, transportation_types: r.transportation_types, currency: r.currency, estimate_days: r.estimate_days })
          })
        })
    },

    getItemType() {
      let token = localStorage.getItem('access_token')
      axios
        .get(this.getItemTypeUrl, { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          this.itemTypeResult = response.data.data
          this.itemTypeResult.forEach((c) => {
            this.itemTypes.push({
              text: c.type_name,
              value: c.type_name,
              shippingPrice: c.type_int_shipping_price
            })
          })
        })
    },

    // Process Order modal (single instance pattern, mirrors allOrder.vue)
    openProcessOrderModal(r) {
      const warehouseObj = this.warehouses.find(w => w.value === r.warehouse_id_fk)
      const enriched = Object.assign({}, r, {
        warehouse: {
          transportation_types: warehouseObj ? warehouseObj.transportation_types : []
        }
      })
      this.currentProcessOrder = enriched
      this.checkAlert = false
      this.clearProcessOrderData()
      this.theQuantity.quantity = r.quantity
      this.updateData.quantity_charge = r.quantity_charge
      this.$nextTick(() => {
        if (this.$refs.processComp) {
          this.$refs.processComp.dialog = true
          // Single-instance pattern bypasses processTheOrder.vue's own gear-icon click
          // handler, which is what normally fetches these — without them exchangeBase (etc.)
          // stays 0, so editing a non-USD warehouse order's item price silently fails to
          // recalculate the total (same bug as allOrder.vue, reproduced on order TR-748).
          this.$refs.processComp.calculateTotalAmount()
          this.$refs.processComp.getExchangeRate()
          this.$refs.processComp.getAEDRate()
          this.$refs.processComp.getCNYRate()
          this.$refs.processComp.getGBPRate()
          this.$refs.processComp.getEURRate()
        }
      })
    },

    calculateTotalAmount() {
      let itemPrice = Number(this.updateData.item_price) || 0
      let shipping = Number(this.updateData.internal_shipping_handling) || 0
      let tax = Number(this.updateData.tax) || 0
      let nationalTax = Number(this.updateData.national_tax) || 0
      let delivery = Number(this.updateData.delivery_price) || 0
      let fee = Number(this.updateData.kreenex_fee) || 0
      let insurance = Number(this.updateData.insurance) || 0
      let qttCharge = Number(this.updateData.quantity_charge) || 0
      let discount = Number(this.updateData.discount) || 0
      let qtt = Number(this.theQuantity.quantity) || 0
      let repackagePrice = Number(this.updateData.repackage_price) || 0
      let holdPrice = Number(this.updateData.hold_price) || 0
      let otherServicePrice = Number(this.updateData.other_service_price) || 0
      const _userData = JSON.parse(localStorage.getItem('user_data') || '{}')
      const _exRate = Number(_userData.ex_rate) || 1
      let localDelivery = (Number(this.updateData.local_delivery) || 0) / _exRate

      // Per-unit shipping, matching newOrder.vue/editOrderCharge.vue: item price and shipping
      // are both charged per unit. Confirmed against live data — 76 of 79 existing orders with
      // qty>1 and shipping>0 match (item_price + shipping) * qty, not this flat variant.
      let total = (itemPrice + shipping) * qtt + tax + nationalTax + delivery + fee + insurance + qttCharge + localDelivery

      if (this.updateData.is_repackage) total += repackagePrice
      if (this.updateData.is_hold) total += holdPrice
      if (otherServicePrice > 0) total += otherServicePrice
      if (discount > 0) total -= discount

      this.updateData.total_amount = total.toFixed(2)
    },

    clearProcessOrderData() {
      this.updateData.item_price = null
      this.updateData.the_tl_price = null
      this.updateData.the_aed_price = null
      this.updateData.the_cny_price = null
      this.updateData.the_gbp_price = null
      this.updateData.the_eur_price = null
      this.updateData.tax = null
      this.updateData.item_weight = null
      this.updateData.internal_shipping_handling = 0
      this.updateData.delivery_price = null
      this.updateData.kreenex_fee = null
      this.updateData.insurance = null
      this.updateData.total_amount = null
      this.updateData.from_date = null
      this.updateData.to_date = null
      this.updateData.quantity_charge = null
      this.updateData.description = ''
      this.updateData.item_type = ''
      this.updateData.item_weight_lb = null
      this.updateData.discount = null
      this.updateData.send_sms = false
      this.updateData.is_repackage = false
      this.updateData.is_hold = false
      this.updateData.other_service = ''
      this.updateData.repackage_price = null
      this.updateData.hold_price = null
      this.updateData.other_service_price = null
      this.updateData.local_delivery = null
      this.updateData.tax = null
      this.updateData.national_tax = null
      this.updateData.process_note = ''
      this.updateData.customer_process_note = ''
      this.updateData.customer_id_fk = null
      this.updateData.product_name = null
      this.updateData.product_url = null
      this.updateData.quantity = null
      this.updateData.color = null
      this.updateData.size = null
      this.updateData.note = null
      this.updateData.brand = null
      this.updateData.order_image = null
      this.checkAlert = false
    },

    processOrder(event, id, stime, stime1, stime2) {
      let token = localStorage.getItem('access_token')
      this.loading = true
      this.updateData.send_sms = this.updateData.send_sms.toString()

      const formData = new FormData()

      Object.keys(this.updateData).forEach(key => {
        if (key === 'customer_process_note_image' || key === 'order_image') return
        if (this.updateData[key] !== null && this.updateData[key] !== undefined) {
          formData.append(key, this.updateData[key])
        }
      })

      if (this.updateData.customer_process_note_image instanceof File) {
        formData.append('customer_process_note_image', this.updateData.customer_process_note_image)
      }

      if (this.updateData.order_image instanceof File) {
        formData.append('order_image', this.updateData.order_image)
      }

      axios.post(this.processOrderUrl + '/' + id, formData, {
        headers: { Authorization: 'Bearer ' + token }
      })
        .then(() => {
          this.loading = false
          this.clearProcessOrderData()
          stime.time = ''
          stime1.time = ''
          stime2.time = ''
          this.checkAlert = true
          this.getResults()
          let b = this
          setTimeout(function () {
            b.checkAlert = false
          }, 1500)
        })
        .catch((error) => {
          const rawMsg = error && error.response && error.response.data && error.response.data.message
          const msg = rawMsg && typeof rawMsg === 'object'
            ? Object.values(rawMsg).flat().join('\n')
            : (rawMsg || this.$t('orders.errorOccurredProcessingOrder'))
          swal(this.$t('common.error'), msg, 'error')
          this.loading = false
          this.clearProcessOrderData()
          stime.time = ''
          stime1.time = ''
          stime2.time = ''
        })
    },

    handleWarehouseFilter(val) {
      this.tableData.warehouse_id_fk = Array.isArray(val) && val.length ? val.join(',') : ''
      this.url = 'api/branch_delivery_order'
      this.getResults()
    },

    handleClearedFilter(val) {
      this.tableData.cleared_filter = val || 'all'
      this.url = 'api/branch_delivery_order'
      this.getResults()
    },

    deliverToUser(data) {
      if (data.credit < 0) {
        swal({
          title: this.$t('common.warning'),
          text: this.$t('orders.customerHasLoanMustPayMessage', { name: data.full_name, amount: data.credit * -1 }),
          icon: 'warning',
          buttons: [this.$t('common.cancel'), this.$t('common.skip')],
        }).then((confirm) => {
          if (confirm) {
            let token = localStorage.getItem('access_token')
            axios
              .put(this.archiveOrderUrl + '/' + data.order_id, '', {
                headers: { Authorization: 'Bearer ' + token },
              })
              .then(() => {
                this.getResults()
                swal({
                  title: this.$t('common.done'),
                  text: '',
                  icon: 'success',
                  timer: 1000,
                  buttons: false,
                })
              })
              .catch((error) => {
                swal(this.$t('common.error'), `${error.response.data.message}`, 'error')
              })
          }
        })
        return
      }

      swal({
        title: this.$t('common.areYouSure'),
        text: this.$t('orders.onceSentWarning'),
        icon: 'warning',
        buttons: true,
      }).then((accept) => {
        if (accept) {
          let token = localStorage.getItem('access_token')
          axios
            .put(this.archiveOrderUrl + '/' + data.order_id, '', {
              headers: { Authorization: 'Bearer ' + token },
            })
            .then((response) => {
              this.getResults()
              swal({
                title: this.$t('common.done'),
                text: '',
                icon: 'success',
                timer: 1500,
                buttons: false,
              })
            })
            .catch((error) => {
              swal(this.$t('common.error'), `${error.response.data.message}`, 'error')
            })
        }
      })
    },

    modal(modal) {
      $('#' + modal).modal({ backdrop: 'static' })
    },

    closeModal(modal) {
      let theModal = $('#' + modal)
      theModal.modal('toggle')
    },

    initDetail(modal, id) {
      let token = localStorage.getItem('access_token')
      axios
        .get(this.orderDetailUrl + '/' + id, { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          let data = response.data
          this.detailResults = data.data
          this.detialData = this.detailResults[0]
        })
      this.modal(modal)
    },

    // Opens the order's linked shipping invoice using AddOrderInvoice.vue's own already-working
    // openShippingInvoiceView() (same component/view "Save and view" and orderInvoices.vue's own
    // "View" action use) rather than duplicating that fetch-and-display logic here. Passes {} so
    // the print log records this as a plain view (matching orderInvoices.vue's own "View"), not
    // openShippingInvoiceView()'s default is_save_and_view flag.
    viewOrderShippingInvoice(r) {
      if (!r.order_invoice_id_fk) return
      this.$refs.addOrderInvoice.openShippingInvoiceView(r.order_invoice_id_fk, {})
    },

    perPageFix(perPage) {
      this.tableData.per_page = perPage
      this.getResults()
    },

    changePage(pageUrl) {
      this.url = pageUrl
      this.getResults()
    },

    searchMe(str) {
      this.tableData.searchOneCol = ''
      this.tableData.searchOneVal = ''
      if (str.trim() !== '') {
        this.tableData.search = str
        this.prevSearchQuery = str
        this.getResults()
      } else {
        swal({
          text: this.$t('common.typeToSearch'),
          icon: 'warning',
          dangerMode: true,
        })
        if (this.prevSearchQuery !== '') {
          this.tableData.search = ''
          this.prevSearchQuery = ''
          this.getResults()
        }
      }
    },

    sortByColumn(str) {
      this.tableData.column = str
      this.tableData.sortKey = str
      this.tableData.sortDir = this.tableData.sortDir === 'desc' ? 'asc' : 'desc'
      this.getResults()
    },

    searchOneCol(columnName, str) {
      this.tableData.searchOneCol = columnName
      this.tableData.searchOneVal = str
      this.getResults()
    },

    filterTable(fromDate, toDate) {
      this.tableData.from_date = fromDate
      this.tableData.to_date = toDate
      this.getResults()
    },

    configPagination(data) {
      this.pagination.lastPage = data.last_page
      this.pagination.currentPage = data.current_page
      this.pagination.total = data.total
      this.pagination.lastPageUrl = data.last_page_url
      this.pagination.nextPageUrl = data.next_page_url
      this.pagination.prevPageUrl = data.prev_page_url
      this.pagination.from = data.from
      this.pagination.to = data.to
    },

    openCancelingOrderModal(orderId, totalAmount) {
      this.cancelOrderModal = true
      this.theCancelOrderID = orderId
      this.cancelRefundAmount = totalAmount || null
    },

    closeCancelingOrderModal() {
      this.cancelOrderModal = false
      this.cancelAlert = false
      this.cancelRefundAmount = null
      this.cancelData.cancel_reason = ''
    },

    cancelingOrder() {
      let token = localStorage.getItem('access_token')
      axios
        .put(this.cancelingOrderUrl + '/' + this.theCancelOrderID, this.cancelData, {
          headers: { Authorization: 'Bearer ' + token }
        })
        .then(() => {
          this.getResults()
          this.cancelAlert = true
          setTimeout(() => {
            this.closeCancelingOrderModal()
          }, 1500)
        })
        .catch(error => {
          swal(this.$t('common.error'), `${error.response.data.message}`, 'error')
        })
    },

    getReasons() {
      let token = localStorage.getItem('access_token')
      axios.get('api/reason/list', { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          let result = response.data
          this.reasons = result.map(r => ({
            text: r.cancel_reason,
            value: r.cancel_reason,
            ar: r.cancel_reason_ar,
            ku: r.cancel_reason_ku
          }))
        })
    },
  },
}
</script>

<style>
.customer-name-stack {
  display: inline-flex;
  flex-direction: column;
  vertical-align: middle;
}
.customer-phone-line {
  display: block;
  font-size: 14px;
  color: #666;
}
.combined-cell-td {
  vertical-align: middle;
}
.combined-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  font-size: 15px;
  line-height: 1.4;
  white-space: nowrap;
}
.combined-cell-label {
  color: #888;
}

@media screen and (max-width: 600px) {
  th {
    font-weight: bold;
    color: #273246;
    padding-left: 5px !important;
  }

  td {
    padding-left: 5px !important;
    padding-top: 10px !important;
    padding-bottom: 10px !important;
  }
}

.table > thead > tr > th, .table > tbody > tr > th, .table > tfoot > tr > th, .table > thead > tr > td, .table > tbody > tr > td, .table > tfoot > tr > td
{
  padding: 0 5px !important;
}

.ship-only-label {
  display: inline-block;
  padding: 2px 8px;
  border: 2px solid green;
  border-radius: 5px;
  font-weight: bold;
  font-size: 0.9em;
  color: green;
}

.panel-heading h4 {
  text-transform: uppercase;
  margin-left: -20px;
  padding-left: 10px;
  font-weight: 500;
}

.icon-enlarge,
.icon-pencil {
  color: navy;
}

.icon-bin2 {
  color: red;
}

.icon-calendar {
  color: green;
}

.btn {
  transition: all 0.5s;
}

.col-xs-12 {
  padding-top: 10px;
  padding-bottom: 10px;
}
.overlay--active:before {
  opacity: 50 !important;
  background: rgba(0, 0, 0, 0.5) !important;
}

.dialog {
  box-shadow: 0 3px 5px -1px rgba(14, 14, 14, 0.212) !important;
  overflow: hidden;
  overflow-y: hidden;
}
.page-stats {
  align-items: center;
  position: relative;
  top: -8px;
  margin-right: 5px;
}

a:hover {
  color: #03a9f4;
  position: relative;
}
a[tooltip]:hover:after {
  content: attr(tooltip);
  padding: 8px 12px;
  color: #ffffff;
  position: absolute;
  right: 80%;
  bottom: 150%;
  white-space: nowrap;
  z-index: 9999;
  -moz-border-radius: 3px;
  -webkit-border-radius: 3px;
  border-radius: 3px;
  -moz-box-shadow: 0px 0px 2px #484b4e;
  -webkit-box-shadow: 0px 0px 2px #484b4e;
  box-shadow: 0px 0px 2px #484b4e;
  background-color: #484b4e;
}

@media screen and (max-width: 767px) {
  .form-group {
    margin-top: 0;
    margin-bottom: 0;
  }
}
/* Mobile order cards: see assets/mobile-order-cards.css */
</style>



// WEBPACK FOOTER //
// src/components/branchDeliveredOrder.vue